export type AnalyticsIdentity = "registered" | "guest" | "visitor";

type AnalyticsValue = string | number | boolean;

export type AnalyticsEventName =
  | "account_auth_code_requested"
  | "analytics_diagnostic"
  | "call_media_changed"
  | "chat_message_sent"
  | "exception"
  | "game_result"
  | "game_start"
  | "guest_play_started"
  | "invite_shared"
  | "login"
  | "matchmaking_joined"
  | "matchmaking_viewed"
  | "operation_failed"
  | "rematch_started"
  | "room_created"
  | "room_joined"
  | "room_rejoined"
  | "select_content"
  | "sign_up"
  | "support_request_submitted"
  | "tutorial_opened"
  | "web_vital";

const eventParameterAllowlist: Record<AnalyticsEventName, ReadonlySet<string>> = {
  account_auth_code_requested: new Set(["method"]),
  analytics_diagnostic: new Set(["source"]),
  call_media_changed: new Set(["media_type", "enabled", "game_id"]),
  chat_message_sent: new Set(["game_id"]),
  exception: new Set(["exception_type", "fatal", "route"]),
  game_result: new Set(["game_id", "result", "versus_ai", "difficulty", "round_number"]),
  game_start: new Set([
    "game_id",
    "versus_ai",
    "difficulty",
    "time_control_seconds",
    "participant_count",
  ]),
  guest_play_started: new Set(["source"]),
  invite_shared: new Set(["method", "game_id"]),
  login: new Set(["method"]),
  matchmaking_joined: new Set(["game_id"]),
  matchmaking_viewed: new Set(["game_id", "waiting_room_count"]),
  operation_failed: new Set(["operation", "game_id", "route"]),
  rematch_started: new Set([
    "game_id",
    "versus_ai",
    "difficulty",
    "time_control_seconds",
    "participant_count",
  ]),
  room_created: new Set(["game_id", "visibility", "identity_type"]),
  room_joined: new Set(["game_id", "identity_type", "join_source", "role"]),
  room_rejoined: new Set(["game_id", "identity_type", "source"]),
  select_content: new Set(["content_type", "item_id", "source"]),
  sign_up: new Set(["method"]),
  support_request_submitted: new Set(["request_kind", "source"]),
  tutorial_opened: new Set(["game_id", "source"]),
  web_vital: new Set(["metric_name", "metric_value", "metric_rating"]),
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export function isAnalyticsDebugMode(
  search = typeof window !== "undefined" ? window.location.search : "",
) {
  return new URLSearchParams(search).get("ga_debug") === "1";
}

function gtag(...args: unknown[]) {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || ((...values: unknown[]) => window.dataLayer?.push(values));
  window.gtag(...args);
}

export function initializeAnalytics() {
  if (typeof document === "undefined") return false;
  return true;
}

export function normalizeAnalyticsPath(pathname: string) {
  const path = pathname.split(/[?#]/, 1)[0] || "/";
  if (/^\/room\/[^/]+\/?$/i.test(path)) return "/room/:roomId";
  if (/^\/join\/[^/]+\/?$/i.test(path)) return "/join/:code";
  if (/^\/how-to-play\/[^/]+\/?$/i.test(path)) return "/how-to-play/:gameId";
  const knownPaths = new Set([
    "/",
    "/about",
    "/app",
    "/billing",
    "/contact",
    "/cookies",
    "/games",
    "/how-to-play",
    "/join",
    "/onboarding",
    "/parental-consent",
    "/privacy",
    "/safety",
    "/settings",
    "/sign-in",
    "/sign-up",
    "/terms",
  ]);
  return knownPaths.has(path) ? path : "/not-found";
}

function cleanValue(value: unknown): AnalyticsValue | undefined {
  if (typeof value === "boolean") return value;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const cleaned = value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_:/.-]/g, "_")
      .slice(0, 80);
    return cleaned || undefined;
  }
  return undefined;
}

export function sanitizeAnalyticsParameters(
  eventName: AnalyticsEventName,
  parameters: Record<string, unknown>,
) {
  const allowed = eventParameterAllowlist[eventName];
  const sanitized: Record<string, AnalyticsValue> = {};
  for (const [key, value] of Object.entries(parameters)) {
    if (!allowed.has(key)) continue;
    const cleaned =
      key === "route" && typeof value === "string"
        ? normalizeAnalyticsPath(value)
        : cleanValue(value);
    if (cleaned !== undefined) sanitized[key] = cleaned;
  }
  return sanitized;
}

export function trackEvent(
  eventName: AnalyticsEventName,
  parameters: Record<string, unknown> = {},
) {
  if (!initializeAnalytics()) return;
  gtag("event", eventName, sanitizeAnalyticsParameters(eventName, parameters));
}

export function trackPageView(pathname: string, title = document.title) {
  if (!initializeAnalytics()) return;
  const pagePath = normalizeAnalyticsPath(pathname);
  gtag("event", "page_view", {
    page_path: pagePath,
    page_location: `${window.location.origin}${pagePath}`,
    page_title: title.slice(0, 100),
  });
}

export function setAnalyticsIdentity(identity: AnalyticsIdentity) {
  if (!initializeAnalytics()) return;
  gtag("set", "user_properties", { identity_type: identity });
}
